import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";

const BalanceStatisticOne = ({ province }) => {
  const [chartData, setChartData] = useState({
    options: {},
    series: []
  });
  const [rawData, setRawData] = useState(null);
  const [metric, setMetric] = useState('users'); // 'users', 'activities', 'plants'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
        
        const response = await fetch(
          `${API_BASE_URL}/api/plantation/pafs/province-wise-department-graph`,
          {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.success && result.data[province]) {
          setRawData(result.data[province]);
        } else {
          // Handle case where province data doesn't exist
          setRawData({});
          setChartData({
            options: {},
            series: []
          });
        }
      } catch (err) {
        setError(err.message);
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [province]);

  // Process data when rawData or metric changes
  useEffect(() => {
    if (!rawData) return;

    // Transform the object into arrays for chart data
    const categories = Object.keys(rawData);
    
    // let yAxisTitle = 'No. of Users';
    let tooltipSuffix = ' users';
    let seriesName = 'Users';

    if (metric === 'activities') {
        // yAxisTitle = 'No. of Plantation Activities';
        tooltipSuffix = ' activities';
        seriesName = 'Active Plantation Activities';
    } else if (metric === 'plants') {
        // yAxisTitle = 'No. of Plants';
        tooltipSuffix = ' plants';
        seriesName = 'Plants';
    }

    const seriesData = Object.values(rawData).map(dept => dept[metric] || 0);
    
    const chartOptions = {
      chart: {
        type: 'bar',
        height: 650,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '15%',
          endingShape: 'rounded',
          borderRadius: 4,
        },
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 1,
        colors: ['transparent']
      },
      xaxis: {
        categories: categories,
        title: {
          text: 'Departments'
        },
        labels: {
          style: {
            fontSize: '11px',
            fontWeight: 500,
          },
          rotate: -45,
          trim: false,
          hideOverlappingLabels: false,
          formatter: function(value) {
            return value;
          },
          maxHeight: 120
        },
        tickPlacement: 'on'
      },
      yaxis: {
        title: {
        // text: yAxisTitle,
        offsetX: 15,
        },
        labels: {
          formatter: function (val) {
            return val.toFixed(0);
          }
        }
      },
      fill: {
        opacity: 1
      },
      colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'],
      tooltip: {
        y: {
          formatter: function (val) {
            return val + tooltipSuffix
          }
        }
      },
      grid: {
        padding: {
          bottom: 50
        }
      },
      responsive: [{
        breakpoint: 768,
        options: {
          plotOptions: {
            bar: {
              columnWidth: '45%'
            }
          },
          xaxis: {
            labels: {
              rotate: -45,
              style: {
                fontSize: '10px'
              },
              maxHeight: 100
            }
          },
          grid: {
            padding: {
              bottom: 60
            }
          }
        }
      }]
    };

    const chartSeries = [{
      name: seriesName,
      data: seriesData
    }];

    setChartData({
      options: chartOptions,
      series: chartSeries
    });
  }, [rawData, metric]);

  if (loading) {
    return (
      <div className='col-12' style={{ marginTop: '16px' }}>
        <div className='card h-100'>
          <div className='card-body'>
            <div className='text-center'>Loading chart data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='col-12' style={{ marginTop: '16px' }}>
        <div className='card h-100'>
          <div className='card-body'>
            <div className='text-center text-danger'>Error: {error}</div>
          </div>
        </div>
      </div>
    );
  }

  // Check if we have data to display
  if (!chartData.series.length || !chartData.series[0].data.length) {
    return (
      <div className='col-12' style={{ marginTop: '16px' }}>
        <div className='card h-100'>
            <div className='card-header d-flex justify-content-between align-items-center'>
                <h6 className='mb-0 fw-bold text-lg'>Departmental Statistics - {province}</h6>
                <div>
                    <select 
                        className="form-select form-select-sm" 
                        value={metric} 
                        onChange={(e) => setMetric(e.target.value)}
                        style={{ minWidth: '150px' }}
                    >
                        <option value="users">User-wise</option>
                        <option value="activities">Activity-wise</option>
                        <option value="plants">Plant-wise</option>
                    </select>
                </div>
            </div>
          <div className='card-body'>
            <div className='text-center mt-4'>No data available for {province}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='col-12' style={{ marginTop: '16px' }}>
      <div className='card h-100'>
        <div className='card-header d-flex justify-content-between align-items-center bg-transparent border-0 pb-0 pt-4 px-4'>
            <h6 className='mb-0 fw-bold text-lg'>Departmental Statistics - {province}</h6>
            <div>
                <select 
                    className="form-select form-select-sm" 
                    value={metric} 
                    onChange={(e) => setMetric(e.target.value)}
                    style={{ minWidth: '150px' }}
                >
                    <option value="users">User-wise</option>
                    <option value="activities">Activity-wise</option>
                    <option value="plants">Plant-wise</option>
                </select>
            </div>
        </div>
        <div className='card-body' style={{ minHeight: '600px' }}>
          
          <div className='mt-40'>
            <div id='balanceStatistics' style={{ minHeight: '500px' }}>
              <ReactApexChart
                options={chartData.options}
                series={chartData.series}
                type='bar'
                height={400}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BalanceStatisticOne;